import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';

import { SortOrder } from '@page/core/types';
import { DAY_IN_MS } from '@page/core/constants';

// Enable plugins for dayjs
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.extend(utc);

export const getDatePartOnlyFromISOString = (isoString: string) => {
  return isoString.split('T')[0];
};

export const parseDateOnly = (underterminedDate: string, fromFormat?: string) => {
  // Convert to dayjs format if needed
  const dayjsFormat = fromFormat ? fromFormat.replace(/yyyy/g, 'YYYY').replace(/dd/g, 'DD') : undefined;

  let date = dayjsFormat ? dayjs.utc(underterminedDate, dayjsFormat) : dayjs.utc(underterminedDate);

  // Check if it's a MM/DD/YYYY format string that wasn't parsed correctly
  if (!date.isValid() || (!dayjsFormat && /^\d{2}\/\d{2}\/\d{4}$/.test(underterminedDate))) {
    date = dayjs.utc(underterminedDate, 'MM/DD/YYYY');

    if (!date.isValid()) date = dayjs.utc();
  }

  // Create a local date with only the date components (no time) - midnight local time
  const dateOnly = dayjs()
    .year(date.year())
    .month(date.month())
    .date(date.date())
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0);

  return dateOnly.toDate();
};

const getDateObjectFromDateOrString = (dateOrString: Date | string) => {
  return typeof dateOrString === 'string' ? parseDateOnly(dateOrString) : dateOrString;
};

export const formatUSDate = (dateOrString: Date | string, full = false) => {
  const dateObject = getDateObjectFromDateOrString(dateOrString);

  return dayjs(dateObject).format(full ? 'MM/DD/YYYY' : 'M/D/YYYY');
};

export const formatRequestDate = (dateOrString: Date | string) => {
  const dateObject = getDateObjectFromDateOrString(dateOrString);

  return dateObject.toLocaleDateString('en-CA');
};

export const formatDate = (dateOrString: Date | string, options?: Intl.DateTimeFormatOptions) => {
  const dateObject = getDateObjectFromDateOrString(dateOrString);

  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', ...options }).format(dateObject);
};

export const formatDateTime = (dateOrString: Date | string, options?: Intl.DateTimeFormatOptions) => {
  const dateObject = getDateObjectFromDateOrString(dateOrString);

  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'medium', ...options }).format(dateObject);
};

export const formatDateToISOString = (date: Date, options: { withTimezone?: boolean } = { withTimezone: false }) => {
  const dayjsDate = dayjs(date);

  if (options.withTimezone) return dayjsDate.format();

  return dayjsDate.toISOString();
};

export const groupObjectsByDate = <T extends Record<string, unknown>>(objects: T[], dateProperty = 'createdAt') => {
  const currentDate = new Date();

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(currentDate.getDate() - 30);

  const twoMonthsAgo = new Date();
  twoMonthsAgo.setMonth(currentDate.getMonth() - 2);

  const previous30DaysGroup: T[] = [];
  const twoMonthsAgoGroup: T[] = [];
  const olderGroup: T[] = [];

  objects.forEach((object) => {
    if (typeof object[dateProperty] !== 'string') return;

    const createdAt = new Date(object[dateProperty] as string);

    if (createdAt >= thirtyDaysAgo) return previous30DaysGroup.push(object);

    if (createdAt >= twoMonthsAgo) return twoMonthsAgoGroup.push(object);

    olderGroup.push(object);
  });

  return {
    previous30DaysGroup,
    twoMonthsAgoGroup,
    olderGroup,
  };
};

export const sortDate = (dateA: Date | string, dateB: Date | string) => {
  return new Date(dateA) > new Date(dateB) ? 1 : -1;
};

export const sortDates = <T extends Date | string>(dates: T[], sortOrder: SortOrder = 'desc') => {
  const sortedDates = dates.toSorted(sortDate);

  if (sortOrder === 'desc') return sortedDates.toReversed();

  return sortedDates;
};

export const getEndOfDay = (date: Date) => {
  const endOfDay = new Date(date);

  endOfDay.setHours(23, 59, 59, 999);

  return endOfDay;
};

export const getStartAndEndOfYear = (year: Date) => {
  const startOfYear = new Date(year);
  const endOfYear = new Date(year);

  startOfYear.setMonth(0, 1);
  startOfYear.setHours(0, 0, 0, 0);

  endOfYear.setMonth(11, 31);
  endOfYear.setHours(23, 59, 59, 999);

  return [startOfYear, endOfYear];
};

export const getStartAndEndOfMonth = (month: Date) => {
  const startOfMonth = new Date(month);
  const endOfMonth = new Date(month);

  startOfMonth.setDate(1);
  startOfMonth.setHours(0, 0, 0, 0);

  endOfMonth.setMonth(endOfMonth.getMonth() + 1, 0);
  endOfMonth.setHours(23, 59, 59, 999);

  return [startOfMonth, endOfMonth];
};

export const getMonthText = (month: number | string) => {
  const monthAsNumber = Number(month);

  if (Number.isNaN(monthAsNumber) || monthAsNumber < 1 || monthAsNumber > 12) return '';

  const monthText = new Date(2021, monthAsNumber - 1, 1).toLocaleString('default', { month: 'long' });

  return monthText;
};

export const getDaysDiff = (dateA: Date, dateB: Date) => {
  const diff = dateA.getTime() - dateB.getTime();

  return diff / DAY_IN_MS;
};

export const getRelativeTimeFromNow = (date: Date | string) => {
  return dayjs(date).fromNow();
};

export const isDateObjectValid = (date: Date) => date.toString() !== 'Invalid Date';

export const isSameDay = (dateA: Date, dateB: Date) => {
  return (
    dateA.getDate() === dateB.getDate() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getFullYear() === dateB.getFullYear()
  );
};

export const isDateInFuture = (date: Date | string) => {
  const dateObject = new Date(date);

  return dateObject > new Date();
};

export const isToday = (date: Date | string) => {
  return dayjs(date).isSame(dayjs(), 'day');
};

export const generateMonthsFromDate = (startDate: Date): Date[] => {
  const dates: Date[] = [];
  const currentDate = new Date();

  const currentMonth = new Date(startDate);

  while (currentMonth <= currentDate) {
    dates.push(new Date(currentMonth));
    currentMonth.setMonth(currentMonth.getMonth() + 1);
  }

  return dates.reverse();
};

export type FormatMonthFormat = 'short' | 'long';

export const formatMonth = (date: Date, format: FormatMonthFormat = 'long') => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const monthName = date.toLocaleDateString('en-US', { month: 'long' });

  if (format === 'short') return `${year}-${String(month + 1).padStart(2, '0')}`;

  return `${monthName} ${year}`;
};

export const addDateTime = (date: Date, amount: number, unit: dayjs.ManipulateType) => {
  return dayjs(date).add(amount, unit).toDate();
};

export const subtractDateTime = (date: Date, amount: number, unit: dayjs.ManipulateType) => {
  return dayjs(date).subtract(amount, unit).toDate();
};
